import { AuthScopedCalendarId } from "../events/calendar-types.ts";
import type { UserId } from "@tento-lona/sheets";
import type { PreferencesStorage } from "./preferences-storage.ts";
import { StoredPreference } from "./stored-preference.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";

/** `CalendarVisibilityPreferences`'s friend record (`Internals`): its constructor registers each instance. */
type CalendarVisibilityPreferencesInternals = {
  readonly store: (prefs: CalendarVisibilityPreferences, acid: AuthScopedCalendarId, visible: boolean) => void;
};
const visibilityPreferencesInternals = new Internals<CalendarVisibilityPreferences, CalendarVisibilityPreferencesInternals>("CalendarVisibilityPreferences");

/** One calendar's stored visibility. */
export type CalendarVisibilityEntry = {
  /** The calendar's account-scoped id in its string form (`AuthScopedCalendarId.key(acid)`). */
  readonly key: string;
  readonly visible: boolean;
};

/**
 * Which of a user's calendars are shown, stored locally per user
 * (`client.preferences.calendarVisibility(userId)`), read-only: its one
 * writer is `client.calendars.setVisible(acid, visible, actor)`, which
 * updates the client's visibility, stores it here and announces
 * `CalendarVisibilityChanged` stamped with the writer — a value stored on its
 * own would leave the client's visibility and the announcement behind.
 */
export class CalendarVisibilityPreferences {
  readonly #storage: PreferencesStorage;
  readonly #userId: string;

  static readonly #internals: CalendarVisibilityPreferencesInternals = {
    store: (prefs, acid, visible) => prefs.#store(acid, visible),
  };

  constructor(storage: PreferencesStorage, userId: UserId) {
    visibilityPreferencesInternals.register(this, CalendarVisibilityPreferences.#internals);
    this.#storage = storage;
    this.#userId = userId;
  }

  #storageKey(): string {
    return `${this.#userId}/calendar-visibility`;
  }

  /**
   * The stored overrides, read synchronously from local storage (nothing is
   * fetched); unusable entries are logged and skipped.
   */
  peekAll(): CalendarVisibilityEntry[] {
    const storageKey = this.#storageKey();
    const list = StoredPreference.array(storageKey, StoredPreference.json(this.#storage, storageKey));
    const entries: CalendarVisibilityEntry[] = [];
    for (const entry of list ?? []) {
      const record = StoredPreference.record(storageKey, entry);
      if (record == null) continue;
      // The stored shape is `{ acid, visible }` (`acid`: the key's string form).
      const key = record["acid"];
      const visible = record["visible"];
      if (typeof key === "string" && typeof visible === "boolean") {
        entries.push({ key, visible });
      } else {
        StoredPreference.unusable(storageKey, "entry is not { acid, visible }", JSON.stringify(entry));
      }
    }
    return entries;
  }

  #store(acid: AuthScopedCalendarId, visible: boolean): void {
    const key = AuthScopedCalendarId.key(acid);
    const entries = this.peekAll().filter((entry) => entry.key !== key);
    entries.push({ key, visible });
    const stored = entries.map((entry) => ({ acid: entry.key, visible: entry.visible }));
    this.#storage.setItem(this.#storageKey(), JSON.stringify(stored));
  }
}

/**
 * Store whether calendar `acid` is shown in `prefs` — the storage half of
 * `CalendarsClient.setVisible`, the one calendar-visibility writer.
 *
 * @internal SDK-private (`CalendarsClient`); `lib.ts` does not export it.
 */
export function writeCalendarVisibility(
  prefs: CalendarVisibilityPreferences,
  acid: AuthScopedCalendarId,
  visible: boolean,
): void {
  visibilityPreferencesInternals.of(prefs).store(prefs, acid, visible);
}
